import { IS_ANDROID } from '@repo/common-utils';
import { useUserEventLogger } from '@repo/tracking-service';
import { Chip, TextField } from '@zenterprise-inc/ui';
import cn from 'clsx';
import Image from 'next/image';
import { SyntheticEvent, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import s from '@/components/tax-refund/cancel/CancelContent.module.scss';
import MarkdownView from '@/components/tax-refund/common/view/MarkdownView';

type ReasonItem = {
  readonly content: string;
  readonly id: string;
  readonly label: string;
  readonly reason: string;
  readonly title: string;
};

type CancelReasonListProps = {
  reasons: ReasonItem[];
  name: string; // 사업체명 또는 납세자명
  selectedIndex: number | null;
  etcContent: string;
  handleChangeSelect: (index: number) => void;
  handleChangeEtcContent: (etcContent: string) => void;
  businessType: 'indis' | 'corps';
};

export function CancelReasonList({
  reasons,
  name,
  selectedIndex,
  etcContent,
  handleChangeSelect,
  handleChangeEtcContent,
  businessType
}: CancelReasonListProps) {
  const { sendClickEvent } = useUserEventLogger();

  const handleChangeText = (e: SyntheticEvent<HTMLTextAreaElement>) => {
    handleChangeEtcContent(e.currentTarget.value);
  };
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const nameKey = businessType === 'indis' ? '{납세자명}' : '{사업체명}';

  useIsomorphicLayoutEffect(() => {
    if (!IS_ANDROID) return () => {};
    const handleResize = () => {
      textRef?.current?.scrollIntoView({ block: 'center' });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const reasonList = reasons.map((item, index) => {
    const content = item.content.replaceAll(nameKey, name).replaceAll('<br>', '\n');
    const handleClick = () => {
      sendClickEvent('cancel_reason_reason', { reason_id: item.reason, business: businessType });
      handleChangeSelect(index);
    };
    const handleClickInput = () => {
      sendClickEvent('cancel_reason_reason-input', { business: businessType });
    };

    if (item.reason.includes('기타')) {
      return (
        <div key={index} className="mb-10">
          <Chip selected={selectedIndex === index} onClick={handleClick}>
            {item.title}
          </Chip>
          <div
            className={cn('relative', {
              [s.show as string]: index === selectedIndex,
              [s.hide as string]: index !== selectedIndex
            })}
          >
            <div className="pt-3" />
            <div className="rounded-2xl bg-gray-50 px-5 py-5 text-gray-800">
              <MarkdownView content={content} />
              <TextField.Textarea
                className="bg-white"
                placeholder="의견남기기"
                maxLength={500}
                value={etcContent}
                onChange={handleChangeText}
                ref={textRef}
                onClick={handleClickInput}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={index} className="mb-5">
        <Chip selected={selectedIndex === index} onClick={handleClick}>
          {item.title}
        </Chip>
        <div
          className={cn('relative', {
            [s.show as string]: index === selectedIndex,
            [s.hide as string]: index !== selectedIndex
          })}
        >
          <div className="absolute right-3 top-0">
            <Image className="rounded-full" src={'/images/incomeTaxRefund/img_cancel-cpa-pro.png'} width={60} height={60} alt={'cpa'} />
          </div>
          <div className="pt-10" />
          <div className="rounded-2xl bg-gray-50 px-5 pb-4 pt-8 text-gray-800">
            <MarkdownView content={content} />
          </div>
        </div>
      </div>
    );
  });

  return <div className="page-padding">{reasonList}</div>;
}
